import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaEdit } from "react-icons/fa"

import { useUpdateClassTemplate } from "@/hooks/api/useClassTemplates"
import { usePermissions } from "@/hooks/usePermissions"
import type {
  ClassTemplateUpdateInput,
  EditClassTemplateProps,
} from "@/types/class_template"
import { class_templateUpdateSchema } from "@/types/class_template"
import { Button } from "@chakra-ui/react"

import ClassTemplateFormDialog from "./ClassTemplateFormDialog"

const EditClassTemplate = memo(
  ({ class_template, onSuccess, onCancel }: EditClassTemplateProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateClassTemplateMutation = useUpdateClassTemplate()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<ClassTemplateUpdateInput>(
      () => ({
        name: class_template.name ?? "",
        slug: class_template.slug ?? "",
        description: class_template.description ?? "",
        program_type: class_template.program_type ?? null,
        instructor_id: class_template.instructor_id ?? "",
        swim_level: class_template.swim_level ?? null,
        location: class_template.location ?? null,
        day_of_week: class_template.day_of_week ?? null,
        start_time: class_template.start_time ?? "",
        end_time: class_template.end_time ?? "",
        duration_minutes: class_template.duration_minutes ?? null,
        max_students: class_template.max_students ?? undefined,
        min_students: class_template.min_students ?? null,
        current_enrollment: class_template.current_enrollment ?? null,
        waitlist_enabled: class_template.waitlist_enabled ?? false,
        price: class_template.price ?? undefined,
        prerequisites: class_template.prerequisites ?? "",
        notes: class_template.notes ?? "",
        is_active: class_template.is_active ?? false,
        is_published: class_template.is_published ?? false,
      }),
      [class_template],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: ClassTemplateUpdateInput) => {
      await updateClassTemplateMutation.mutateAsync({
        class_templateId: class_template.id,
        class_templateData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("class_templates:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit classTemplate: ${class_template.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <ClassTemplateFormDialog
          isOpen={isOpen}
          title="Edit Class"
          submitLabel="Update Class"
          /* TODO: zodResolver generic mismatch with react-hook-form Resolver type */
          resolver={zodResolver(class_templateUpdateSchema) as Resolver<ClassTemplateUpdateInput>}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateClassTemplateMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditClassTemplate.displayName = "EditClassTemplate"

export default EditClassTemplate
export type { EditClassTemplateProps }
